/**
 * Token-bound compact surface box for monograph demo panels.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { cn } from "../styling/index.js";
import { colorWithAlpha } from "./MonographPanelColor.js";
import type { MonographPanelTokens } from "./MonographPanelPrimitiveTypes.js";

export type MonographSurfaceBoxElement = "article" | "div" | "li" | "section" | "span";
export type MonographSurfaceBoxFont = "body" | "mono";
export type MonographSurfaceBoxTone =
  | "neutral"
  | "accent"
  | "success"
  | "warning"
  | "danger"
  | "info";

export interface MonographSurfaceBoxProps {
  readonly tokens: MonographPanelTokens;
  readonly children: React.ReactNode;
  readonly as?: MonographSurfaceBoxElement;
  readonly tone?: MonographSurfaceBoxTone;
  readonly active?: boolean;
  readonly color?: string;
  readonly background?: string;
  readonly borderColor?: string;
  readonly textColor?: string;
  readonly className?: string;
  readonly style?: React.CSSProperties;
  readonly role?: React.AriaRole;
  readonly ariaLabel?: string;
  readonly ariaLive?: "off" | "polite" | "assertive";
  readonly compact?: boolean;
  readonly padding?: React.CSSProperties["padding"];
  readonly radius?: number;
  readonly display?: React.CSSProperties["display"];
  readonly gap?: React.CSSProperties["gap"];
  readonly alignItems?: React.CSSProperties["alignItems"];
  readonly justifyContent?: React.CSSProperties["justifyContent"];
  readonly textAlign?: React.CSSProperties["textAlign"];
  readonly font?: MonographSurfaceBoxFont;
  readonly gridTemplateColumns?: React.CSSProperties["gridTemplateColumns"];
  readonly minHeight?: React.CSSProperties["minHeight"];
  readonly overflow?: React.CSSProperties["overflow"];
  readonly shadow?: boolean;
}

function toneColor(tokens: MonographPanelTokens, tone: MonographSurfaceBoxTone): string | undefined {
  if (tone === "accent") return tokens.accent;
  if (tone === "success") return tokens.accentGreen ?? tokens.accentSafe ?? "#22c55e";
  if (tone === "warning") return tokens.accentAmber ?? "#f59e0b";
  if (tone === "danger") return tokens.accentRed ?? tokens.accentForbidden ?? "#ef4444";
  if (tone === "info") return tokens.accentBlue ?? tokens.accent;
  return undefined;
}

export function MonographSurfaceBox({
  tokens,
  children,
  as: Component = "div",
  tone = "neutral",
  active = false,
  color,
  background,
  borderColor,
  textColor,
  className,
  style,
  role,
  ariaLabel,
  ariaLive,
  compact = false,
  padding,
  radius = 10,
  display = "block",
  gap,
  alignItems,
  justifyContent,
  textAlign,
  font = "body",
  gridTemplateColumns,
  minHeight,
  overflow,
  shadow = false,
}: MonographSurfaceBoxProps): React.JSX.Element {
  const toneHue = color ?? toneColor(tokens, tone);
  const resolvedBorder =
    borderColor ??
    (toneHue
      ? colorWithAlpha(toneHue, active ? "77" : "55")
      : tokens.border);
  const resolvedBackground =
    background ??
    (toneHue
      ? colorWithAlpha(toneHue, active ? "1c" : "10")
      : colorWithAlpha(tokens.void, "70"));

  return (
    <Component
      role={role}
      aria-label={ariaLabel}
      aria-live={ariaLive}
      className={cn("csk-monograph-surface-box", className)}
      style={{
        alignItems,
        background: resolvedBackground,
        border: `1px solid ${resolvedBorder}`,
        borderRadius: radius,
        boxShadow: shadow && toneHue ? `0 0 14px ${colorWithAlpha(toneHue, "24")}` : undefined,
        color: textColor ?? tokens.text,
        display,
        fontFamily: font === "mono" ? tokens.mono : undefined,
        gap,
        gridTemplateColumns,
        justifyContent,
        minHeight,
        overflow,
        padding: padding ?? (compact ? "8px 10px" : "10px 12px"),
        textAlign,
        transition: "background 140ms ease, border-color 140ms ease, box-shadow 140ms ease",
        ...style,
      }}
    >
      {children}
    </Component>
  );
}
